<template>
    <div>
        <div v-if="!['NL', 'BE'].includes(currentCompany.country)">
            <b-alert show class="gk-alert mt-2" variant="warning">
                {{ $gettext('PEPPOL e-invoicing is only available for companies based in the Netherlands and Belgium. Your current company country is set to') }} <strong>{{ currentCompany.country }}</strong>.
            </b-alert>
        </div>
        
        <div v-if="['NL', 'BE'].includes(currentCompany.country)">
            <BankConnectionModal :show-modal="showBankConnectionModal" @close="showBankConnectionModal = false; loading = false" :existingBeIban="existingBeIban" />

            <EInvoiceMessageModal 
                :showModal="showEInvoiceMessageModal"
                :title="eInvoiceMessageTitle"
                :message="eInvoiceMessageText"
                :showAnimation="eInvoiceModalAnimation"
                @close="onEInvoiceMessageClose"
            />

            <div v-if="fetchingInitialData" class="connection-details-container">
                <b-skeleton animation="throb" width="85%"></b-skeleton>
                <b-skeleton animation="throb" width="55%"></b-skeleton>
                <b-skeleton animation="throb" width="70%"></b-skeleton>
                <br />
                <b-skeleton animation="throb" width="85%"></b-skeleton>
                <b-skeleton animation="throb" width="55%"></b-skeleton>
                <b-skeleton animation="throb" width="70%"></b-skeleton>
                <br />
                <b-skeleton animation="throb" width="85%"></b-skeleton>
                <b-skeleton animation="throb" width="55%"></b-skeleton>
                <b-skeleton animation="throb" width="70%"></b-skeleton>
            </div>
            
            <div v-if="!fetchingInitialData">
                <div v-if="!currentCompany.companysettings.is_peppol_company_authorized && !currentCompany.companysettings.are_peppol_credentials_saved">
                    <h3>{{ $gettext('Not Connected') }}</h3>

                    <div v-if="selectedAccount" class="connection-details-container">
                        <b-form ref="peppolform" @submit.prevent="submitConnectionDetails">
                            <div class="modal-form-legend">
                                <legend v-translate>Fill in your company details</legend>
                            </div>

                            <b-form-row>
                                <b-col cols="7" lg="8">
                                    <b-form-group id="input-group-1" label-class="gk-label-margin gk-modal-label" label-for="input-1">
                                        <template slot="label">
                                            <span v-translate>Bank Account</span>
                                        </template>
                                        <b-input-group>
                                            <BankAccountSelector />
                                        </b-input-group>
                                    </b-form-group>
                                </b-col>

                                <b-col cols="5" lg="4">
                                    <b-form-group id="input-group-2" label-class="gk-label-margin gk-modal-label" label-for="input-2">
                                        <template slot="label">
                                            <span v-translate>Status</span>
                                        </template>
                                        <b-input-group>
                                            <b-badge variant="primary" class="p-1.5" pill style="font-size: 0.9rem;">
                                                <font-awesome-icon icon="fa fa-check" />&nbsp;Connected
                                            </b-badge>
                                        </b-input-group>
                                    </b-form-group>
                                </b-col>
                            </b-form-row>

                            <b-form-row>
                                <b-col cols="7" lg="8">
                                    <b-form-group id="input-group-3" label-class="gk-label-margin gk-modal-label" label-for="input-3">
                                        <template slot="label">
                                            <span v-translate>Company Name</span>
                                        </template>
                                        <b-input-group>
                                            <b-form-input id="input-3" type="text" v-model="connectionDetails.companyName" :required="true" :disabled="loading" :placeholder="$gettext('My Company')" />
                                            <b-alert v-if="connectionDetails.companyName !== connectionDetails.bankAccountName" show class="gk-alert mt-2" variant="warning">
                                                {{ $gettext('Your company name does not match your bank account name. Both fields must have the same values before making a connection to the PEPPOL network.') }}
                                                <GekkoButton
                                                    @click="synchronizeNames()"
                                                    :text="$gettext('Synchronize Names')"
                                                    icon="fa-solid fa-arrows-rotate"
                                                    variant="link"
                                                    class="text-info"
                                                    :disabled="loading"
                                                />
                                            </b-alert>
                                        </b-input-group>
                                    </b-form-group>
                                </b-col>

                                <b-col cols="5" lg="4">
                                    <b-form-group id="input-group-4" label-class="gk-label-margin gk-modal-label" label-for="input-4">
                                        <template slot="label">
                                            <span v-translate>{{ coc_label }}</span>
                                        </template>
                                        <b-input-group>
                                            <b-form-input id="input-4" type="text" v-model="connectionDetails.kvk" :required="true" :disabled="loading" placeholder="123456789" />
                                            <GekkoButton
                                                id="import-from-kvk-button"
                                                v-if="showImportButton"
                                                @click="importFromKVK()"
                                                :text="kvkButtonText"
                                                :icon="kvkButtonIcon"
                                                variant="link"
                                                class="text-info"
                                                :disabled="loading"
                                            />

                                            <DropdownContainer
                                                :showContainer="showKvkSuggestionTable"
                                                :preventCloseElementIds="['import-from-kvk-button', 'form-input-name']"
                                                @closeDropdown="onKvkDropdownClose"
                                            >
                                                <KvkSuggestionsTable
                                                    ref="kvk_suggestion_table"
                                                    :companyName="connectionDetails.companyName"
                                                    @companySelected="onKvkCompanySelected"
                                                />
                                            </DropdownContainer>
                                        </b-input-group>
                                    </b-form-group>
                                </b-col>
                            </b-form-row>

                            <b-form-row>
                                <b-col cols="7" lg="8">
                                    <b-form-group id="input-group-5" label-class="gk-label-margin gk-modal-label" label-for="input-5">
                                        <template slot="label">
                                            <span v-translate>Address</span>
                                        </template>
                                        <b-input-group>
                                            <b-form-input id="input-5" type="text" v-model="connectionDetails.address" :required="true" :disabled="loading" :placeholder="$gettext('Mainstreet 123')" />
                                        </b-input-group>
                                    </b-form-group>
                                </b-col>

                                <b-col cols="5" lg="4">
                                    <b-form-group id="input-group-6" label-class="gk-label-margin gk-modal-label" label-for="input-6">
                                        <template slot="label">
                                            <span v-translate>Postal Code</span>
                                        </template>
                                        <b-input-group>
                                            <b-form-input id="input-6" type="text" v-model="connectionDetails.postal_code" :required="true" :disabled="loading" :placeholder="$gettext('1000AA')" />
                                        </b-input-group>
                                    </b-form-group>
                                </b-col>
                            </b-form-row>

                            <b-form-row>
                                <b-col cols="7" lg="8">
                                    <b-form-group id="input-group-7" label-class="gk-label-margin gk-modal-label" label-for="input-7">
                                        <template slot="label">
                                            <span v-translate>City</span>
                                        </template>
                                        <b-input-group>
                                            <b-form-input id="input-7" type="text" v-model="connectionDetails.city" :required="true" :disabled="loading" :placeholder="$gettext('Amsterdam')" />
                                        </b-input-group>
                                    </b-form-group>
                                </b-col>

                                <b-col cols="5" lg="4">
                                    <b-form-group id="input-group-8" label-class="gk-label-margin gk-modal-label" label-for="input-8">
                                        <template slot="label">
                                            <span v-translate>Country</span>
                                        </template>
                                        <b-input-group>
                                            <b-form-input id="input-8" type="text" v-model="connectionDetails.country" :disabled="true" />
                                        </b-input-group>
                                    </b-form-group>
                                </b-col>
                            </b-form-row>

                            <b-form-row>
                                <b-col>
                                    <b-form-group id="input-group-9" label-for="input-9" label-class="gk-label-margin gk-modal-label">
                                        <template slot="label">
                                            <span v-translate>Email</span>&nbsp;
                                            <font-awesome-icon id="tt_company_email" icon="fa-solid fa-circle-info" class="text-info" />
                                            <b-tooltip target="tt_company_email" triggers="hover">
                                            {{ $gettext('The email address from which you send your invoices.') }}
                                            </b-tooltip>
                                        </template>
                                        <b-form-input id="input-9" v-model="connectionDetails.email" type="email" :placeholder="$gettext('john.doe@myemail.com')" :required="true" :disabled="loading" />
                                        
                                        <GekkoButton
                                            v-if="!connectionDetails.email || connectionDetails.email.length < 1"
                                            @click="useUserEmail()"
                                            :text="$gettext('Use your user email')"
                                            variant="link"
                                            class="text-info"
                                            :disabled="loading"
                                        />
                                    </b-form-group>
                                </b-col>
                            </b-form-row>

                            <b-form-row>
                                <b-col>
                                    <b-form-group id="input-group-10" label-for="input-10">
                                        <div style="display: flex;">
                                            <b-form-checkbox id="input-10" v-model="connectionDetails.termsAccepted" :required="true" :disabled="loading" />
                                            <span>
                                                {{$gettext('You must read and accept the')}}&nbsp;<a href="https://maventa.com/terms-and-conditions#english" target="_blank">{{ $gettext('Terms and Conditions') }}</a>&nbsp;{{$gettext('before connecting to the PEPPOL network.')}}
                                            </span>
                                        </div>
                                    </b-form-group>
                                </b-col>
                            </b-form-row>

                            <div style="position: relative;">
                                <GekkoButton 
                                    type="submit"
                                    :text="$gettext('Connect')" 
                                    :disabled="(connectionDetails.companyName !== connectionDetails.bankAccountName) || loading || !connectionDetails.termsAccepted"
                                    :class="loading ? 'loading-spinner-button' : ''"
                                />
                                <LoadingSpinner v-if="loading" />
                            </div>
                        </b-form>
                    </div>

                    <div v-if="!selectedAccount" class="connection-details-container">
                        <b-alert v-if="connectionDetails.companyName.length === 0 || connectionDetails.companyName !== connectionDetails.bankAccountName" show class="gk-alert mt-2" variant="warning">
                            {{ $gettext('You need to connect a bank account before connecting to PEPPOL so we can verify your identity.') }}
                        </b-alert>
                        <GekkoButton 
                            @click="showBankConnectionModal = true" 
                            :text="$gettext('Connect Bank')" 
                        />
                    </div>
                </div>
                
                <div v-if="currentCompany.companysettings.is_peppol_company_authorized && currentCompany.companysettings.are_peppol_credentials_saved" class="connection-details-container">
                    <h3><font-awesome-icon icon="fa fa-check" class="text-primary" />&nbsp;{{ $gettext('Connected') }}</h3>
                    <p>
                        {{ $gettext('You can now send electronic invoices via PEPPOL') }}
                        <br />
                        <a :href="Urls['invoices_add_invoice_step1']()" class="text-info">
                            {{ $gettext('Create new invoice') }}
                        </a>
                    </p>
                    
                    <div v-if="peppolCompanyInformation">
                        <ul class="list-group">
                            <li class="list-group-item">
                                <strong>{{ $gettext('Company name') }}:</strong> {{ peppolCompanyInformation.name }}
                            </li>
                            <li class="list-group-item">
                                <strong>{{ $gettext('Peppol ID') }}:</strong> {{ `${peppolIdPrefix}${peppolCompanyInformation.bid}` }}
                            </li>
                        </ul>
                    </div>
                    <br>

                    <i>{{ $gettext('This is the account you created via Gekko on Peppol. If there is anything wrong with your account, please contact support@getgekko.com. Any invoice send via Peppol is subject to the') }}&nbsp;<a href="https://maventa.com/terms-and-conditions#english" target="_blank">{{ $gettext('Terms and Conditions') }}</a>&nbsp;{{ $gettext('of Maventa') }}.</i>
                    
                    <br><br>
                    <div style="position: relative;">
                        <GekkoButton 
                            @click="deactivatePeppolConnection"
                            id="deactivate-connection-button"
                            :text="$gettext('Deactivate Connection')" 
                            :disabled="loading"
                            variant="danger"
                        />
                        <LoadingSpinner v-if="loading" />
                    </div>
                </div>

                <div v-if="!currentCompany.companysettings.is_peppol_company_authorized && currentCompany.companysettings.are_peppol_credentials_saved" class="connection-details-container">
                    <h3><font-awesome-icon icon="fa fa-link-slash" class="text-info" />&nbsp;{{ $gettext('Connection Deactivated') }}</h3>
                    <p>
                        {{ $gettext('You can no longer send electronic invoices via PEPPOL. Click the button below to reactivate your connection and resume sending invoices.') }}
                    </p>
                    <hr>
                    <div style="position: relative;">
                        <GekkoButton 
                            @click="reactivatePeppolConnection" 
                            :text="$gettext('Reactivate Connection')" 
                            :disabled="loading"
                            variant="primary"
                        />
                        <LoadingSpinner v-if="loading" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex"
import settingsPageSetup from "@/mixins/settingsPageSetup"
import BankConnectionModal from "@/components/bank/ui/BankConnectionModal"
import CompanySettingService from "@/store/services/companysetting.service"
import EInvoiceMessageModal from "@/components/invoices/ui/EInvoiceMessageModal.vue"
import KvkSuggestionsTable from '@/components/contacts/KvkSuggestionsTable'
import DropdownContainer from '@/components/ui/DropdownContainer'
import GekkoButton from "@/components/GekkoButton"
import CompanyService from '@/store/services/company.service'
import BankAccountSelector from "@/components/bank/ui/BankAccountSelector"
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"

export default {
    name: "PeppolSettings",
    mixins: [settingsPageSetup],
    components: {
        EInvoiceMessageModal,
        GekkoButton,
        BankConnectionModal,
        KvkSuggestionsTable,
        DropdownContainer,
        BankAccountSelector,
        LoadingSpinner
    },
    data() {
        return {
            loading: false,
            fetchingInitialData: true,
            showEInvoiceMessageModal: false,
            eInvoiceMessageTitle: "",
            eInvoiceMessageText: "",
            eInvoiceModalAnimation: null,
            pageSettings: {
                title: this.$gettext("Peppol Settings"),
                showCompanyPicker: true
            },
            showBankConnectionModal: false,
            showKvkSuggestionTable: false,
            kvkButtonText: this.$gettext('Import from KVK'),
            kvkButtonIcon: 'fa-solid fa-file-import',
            existingBeIban: null, // used for unifiedpost, we may be able to remove it probably
            connectionDetails: {
                bankAccountName: '',
                companyName: '',
                kvk: '',
                address: '',
                postal_code: '',
                city: '',
                country: '',
                email: '',
                termsAccepted: false
            },
            peppolCompanyInformation: null
        }
    },
    watch: {
        currentCompany() {
            if (this.currentCompany) {
                this.connectionDetails.companyName = this.currentCompany.name
                this.connectionDetails.kvk = this.currentCompany.kvk
                this.connectionDetails.address = this.currentCompany.address
                this.connectionDetails.postal_code = this.currentCompany.postal_code
                this.connectionDetails.city = this.currentCompany.city
                this.connectionDetails.country = this.currentCompany.country
                this.connectionDetails.email = this.currentCompany.email
            }
        },
        selectedAccount() {
            if (this.selectedAccount) {
                this.connectionDetails.bankAccountName = this.selectedAccount.name
            }
        }
    },
    computed: {
        ...mapGetters({
            currentCompany: "main/getCurrentCompany",
            selectedAccount: "bankStore/getSelectedAccount",
            bankAccounts: "bankStore/getBankAccounts"
        }),
        showImportButton() {
            return (
                !this.connectionDetails.kvk.length && this.currentCompany.country === 'NL' && this.connectionDetails.companyName.length > 2
            ) // 3 characters required to show button
        },
        coc_label() {
            if (this.currentCompany.coc_label) {
                return this.currentCompany.coc_label
            }
            return this.$gettext('KvK number')
        },
        peppolIdPrefix() {
            if (this.currentCompany.country === 'BE') {
                return '0208:'
            } else {
                return '0106:'
            }
        }
    },
    created () {
        if ("consent_id" in this.$route.query) {  // Enablenow redirect
            this.enablenowGrantConsentSuccess(this.$route.query)
            .catch(() => {
                this.$store.commit('setAlert', {
                    title: this.$gettext("Failed to refresh"),
                    text: this.$gettext("We could not complete your bank connection process. Please try again later or contact support."),
                    variant: "error",
                    duration: 4
                })
            }).finally(() => {
                this.setupData()
            })
        } else {
            this.setupData()
        }
    },
    methods: {
        ...mapMutations({ setSelectedAccount: 'bankStore/SET_SELECTED_ACCOUNT' }),
        ...mapActions({
            loadCurrentCompany: "main/loadCurrentCompany",
            loadBankAccounts: "bankStore/loadBankAccounts",
            enablenowGrantConsentSuccess: "bankStore/enablenowGrantConsentSuccess"
        }),
        setupData() {
            this.loadBankAccounts().then(() => {
                this.setSelectedAccount(this.bankAccounts.length ? this.bankAccounts[0].id : null)
            })

            this.loadCurrentCompany().finally(() => {
                if (this.currentCompany.companysettings && 
                    this.currentCompany.companysettings.is_peppol_company_authorized && 
                    this.currentCompany.companysettings.are_peppol_credentials_saved) {
                        this.checkConnectionDetails()
                } else {
                    this.fetchingInitialData = false
                }
            })
        },
        importFromKVK() {
            if (this.$store.getters['getIsPremium']) {
                this.showKvkSuggestionTable = true
                this.$refs.kvk_suggestion_table.getCompanyList()
                this.kvkButtonText = this.$gettext('Refresh')
                this.kvkButtonIcon = 'fa-solid fa-undo'
            } else {
                this.$store.commit('togglePremiumModal', 'web_invoices')
            }
        },
        onKvkDropdownClose() {
            this.showKvkSuggestionTable = false
            this.$refs.kvk_suggestion_table.clearCompanyList()
            this.kvkButtonText = this.$gettext('Import from KVK')
            this.kvkButtonIcon = 'fa-solid fa-file-import'
        },
        onKvkCompanySelected(company) {
            this.connectionDetails.name = company.naam
            this.connectionDetails.city = company.plaats
            this.connectionDetails.address = company.straatnaam
            this.connectionDetails.kvk = company.kvkNummer
            
            let params = { url: company.links.find((x) => x.rel === 'basisprofiel').href }
            CompanyService.getKvkPageByUrl(params).then((response) => {
                if (
                    'hoofdvestiging' in response.data['_embedded'] &&
                    'adressen' in response.data['_embedded']['hoofdvestiging']
                ) {
                    let address = response.data['_embedded']['hoofdvestiging']['adressen'][0]

                    let street_address = ''
                    if (address['straatnaam']) street_address = address['straatnaam']
                    if (address['huisnummer']) street_address = street_address + ' ' + address['huisnummer']
                    if (address['huisnummertoevoeging']) street_address = street_address + '-' + address['huisnummertoevoeging']

                    this.connectionDetails.address = street_address
                    this.connectionDetails.postal_code = address['postcode']
                    this.connectionDetails.city = address['plaats']
                }
            })

            this.onKvkDropdownClose()
        },
        synchronizeNames() {
            CompanyService.update({
                id: this.currentCompany.id,
                name: this.connectionDetails.bankAccountName,
                country: this.currentCompany.country
            }).then(() => {
                this.connectionDetails.companyName = this.connectionDetails.bankAccountName
            }).catch((error) => {
                let msg = this.$gettext("Something went wrong when trying to set your company name.")
                
                if (error.data && error.data.name) {
                    msg = this.$gettext("You already have a company with this name. Please choose another name.")
                }

                this.$store.commit('setAlert', {
                    title: this.$gettext("Error synchronizing names"),
                    text: msg,
                    variant: 'danger',
                    duration: 4
                })
            })
        },
        useUserEmail() {
            this.connectionDetails.email = this.$store.getters['main/getCurrentUser'].email
        },
        deactivatePeppolConnection() {
            const ConfirmModalConstructor = this.$options.components['confirm-modal']
            const confirmModalInstance = new ConfirmModalConstructor({ propsData: {
                title: this.$gettext('Deactivate Connection'),
                message: this.$gettext('Are you sure you want to deactivate your PEPPOL connection? This will prevent you from sending electronic invoices through the PEPPOL network until you reactivate it.'),
                options: {
                    variant: 'danger',
                    confirmText: this.$gettext('Deactivate')
                }
            }})
            confirmModalInstance.$mount()
            document.body.appendChild(confirmModalInstance.$el)
            confirmModalInstance.$on('confirmed', () => {
                this.loading = true

                if (confirmModalInstance && confirmModalInstance.$el.parentNode) {
                    confirmModalInstance.$el.parentNode.removeChild(confirmModalInstance.$el)
                }
                
                CompanySettingService.deactivatePeppolConnection(this.currentCompany.companysettings.id).then(() => {
                    this.loadCurrentCompany().then(() => { 
                        this.$store.commit('setAlert', {
                            title: this.$gettext("Connection Deactivated"),
                            text: this.$gettext('Your PEPPOL connection was deactivated successfully!'),
                            variant: 'warning',
                            duration: 4
                        })
                        this.loading = false
                    })
                })
            })
            confirmModalInstance.$on('cancel', () => {
                if (confirmModalInstance && confirmModalInstance.$el.parentNode) {
                    confirmModalInstance.$el.parentNode.removeChild(confirmModalInstance.$el)
                }
            })
        },
        reactivatePeppolConnection() {
            this.loading = true
            CompanySettingService.reactivatePeppolConnection(this.currentCompany.companysettings.id).then(() => {
                this.loadCurrentCompany().then(() => { 
                    this.$store.commit('setAlert', {
                        title: this.$gettext("Connection Reactivated"),
                        text: this.$gettext('Your PEPPOL connection was reactivated successfully!'),
                        variant: 'success',
                        duration: 4
                    })
                })
            }).catch(() => {
                this.$store.commit('setAlert', {
                    title: this.$gettext("Error Reactivating Connection"),
                    text: this.$gettext('Your PEPPOL credentials are no longer valid. Please contact support to resolve this issue.'),
                    variant: 'danger',
                    duration: 4
                })
            }).finally(() => {
                this.loading = false
            })
        },
        onEInvoiceMessageClose() {
            this.showEInvoiceMessageModal = false
            this.eInvoiceMessageTitle = ''
            this.eInvoiceMessageText = ''
            this.eInvoiceModalAnimation = null
        },
        submitConnectionDetails() {
            this.loading = true
            const data = {
                company_name: this.connectionDetails.companyName,
                bank_account_id: this.selectedAccount.id,
                ...this.connectionDetails
            }
            
            CompanySettingService.connectToPeppol(this.currentCompany.companysettings.id, data)
                .then(() => {
                    this.showEInvoiceMessageModal = true
                    this.eInvoiceMessageTitle = this.$gettext("Connected to Peppol")
                    this.eInvoiceMessageText = this.$gettext("You have successfully connected to the Peppol network.")
                    this.eInvoiceModalAnimation = 'success'
                    this.checkConnectionDetails()
                })
                .catch((error) => {
                    this.showEInvoiceMessageModal = true
                    this.eInvoiceMessageTitle = this.$gettext("Error connecting to Peppol Network")
                    this.eInvoiceMessageText = (error.data && error.data.detail) ? error.data.detail 
                        : this.$gettext("An unexpected error occurred while attempting to connect your company to the Peppol network. Try again later or contact support if the issue persists.")
                    this.eInvoiceModalAnimation = 'error'
                }).finally(() => {
                    this.loadCurrentCompany().then(() => {
                        this.loading = false
                    })
                })
        },
        checkConnectionDetails() {
            CompanySettingService.checkPeppolConnection(this.currentCompany.companysettings.id).then((response) => {
                this.peppolCompanyInformation = response.data
            }).finally(() => {
                this.fetchingInitialData = false
            })
        }
    }
}
</script>

<style lang="scss" scoped>
.connection-details-container {
    background-color: white;
    border-radius: 20px;
    border: 1px solid #ccc;
    padding: 20px;
    max-width: 600px;
}

.loading-spinner-button {
  .loading-spinner {
    width: auto !important;
  }
}
</style>